/** Código compacto sin espacios: N01 381 58 → N0138158 */
export const formatCompactProductCode = (code) =>
  String(code ?? '')
    .replace(/\s+/g, '')
    .toUpperCase();

/** Divide el código en 4 partes: 3 + 3 + 3 dígitos y el resto en la cuarta. */
export const parseProductCode = (code) => {
  const raw = String(code ?? '').trim();
  if (!raw) {
    return { code1: '', code2: '', code3: '', code4: '' };
  }

  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length >= 4) {
    return {
      code1: tokens[0],
      code2: tokens[1],
      code3: tokens[2],
      code4: tokens.slice(3).join(' '),
    };
  }

  const compact = raw.replace(/\s/g, '').toUpperCase();
  return {
    code1: compact.slice(0, 3),
    code2: compact.slice(3, 6),
    code3: compact.slice(6, 9),
    code4: compact.slice(9),
  };
};

export const pruneSessionItems = (template, session) => {
  if (!session?.items || !template?.categories) return session;
  const validIds = new Set();
  template.categories.forEach((cat) => {
    cat.rows.forEach((row) => validIds.add(row.id));
  });
  const items = {};
  validIds.forEach((id) => {
    if (session.items[id]) items[id] = session.items[id];
  });
  return { ...session, items };
};

/**
 * Para guardar: descarta los productos que quedaron en 'pending' (nunca se
 * tocaron). Al recargar, lo que no está en la sesión guardada se vuelve a
 * tomar como "no modificado" (pending, con el stock del sistema), así que no
 * hace falta persistirlos — con miles de productos sin tocar, esto reduce
 * drásticamente el tamaño del guardado.
 */
export const pruneUnmodifiedItems = (session) => {
  if (!session?.items) return session;
  const items = {};
  Object.entries(session.items).forEach(([id, item]) => {
    if (item?.status && item.status !== 'pending') items[id] = item;
  });
  return { ...session, items };
};

/** Elimina filas duplicadas por productId (evita totales inflados). */
export const normalizeControlTemplate = (template) => {
  if (!template?.categories) return template;
  const seen = new Set();
  const categories = template.categories
    .map((cat) => ({
      ...cat,
      rows: (cat.rows || []).filter((row) => {
        const pid = Number(row.productId);
        if (!Number.isFinite(pid)) return true;
        if (seen.has(pid)) return false;
        seen.add(pid);
        return true;
      }),
    }))
    .filter((c) => c.rows.length > 0);
  return { ...template, categories };
};

/** Arma el listado de control con todos los productos habilitados del catálogo. */
export const buildControlTemplateFromProducts = (products = []) => {
  const byCategory = new Map();
  const seenProductIds = new Set();

  products.forEach((p) => {
    const productId = Number(p.productId ?? p.id);
    if (!Number.isFinite(productId) || seenProductIds.has(productId)) return;
    seenProductIds.add(productId);

    const catName = p.categoryName || 'Sin categoría';
    if (!byCategory.has(catName)) {
      byCategory.set(catName, {
        id: catName.replace(/[^\w-]+/g, '-').slice(0, 60) || 'sin-categoria',
        name: catName,
        descriptionHeader: 'Descripción',
        rows: [],
      });
    }

    const { code1, code2, code3, code4 } = parseProductCode(p.code);
    const qty = Number(p.quantity) || 0;

    byCategory.get(catName).rows.push({
      id: `product-${productId}`,
      productId,
      model: p.modelName || 'Sin modelo',
      description: p.name ?? '',
      code1,
      code2,
      code3,
      code4,
      productCode: p.code ?? '',
      compactCode: formatCompactProductCode(p.code),
      shelfLetter: p.shelfLetter ?? '',
      shelfNumber: p.shelfNumber ?? '',
      zone: p.zone ?? '',
      systemStock: qty,
      displayStock: qty,
      stockSource: p.stockSource || 'none',
      primaryLotId: p.primaryLotId ?? null,
      lots: p.lots || [],
    });
  });

  byCategory.forEach((cat) => {
    cat.rows.sort((a, b) => {
      const mod = (a.model || '').localeCompare(b.model || '', 'es');
      if (mod !== 0) return mod;
      return (a.description || '').localeCompare(b.description || '', 'es');
    });
  });

  const categories = Array.from(byCategory.values()).filter((c) => c.rows.length > 0);

  return normalizeControlTemplate({
    version: 4,
    title: 'CONTROL DE REPUESTOS',
    categories,
  });
};

export const createInitialSessionState = (template) => {
  const items = {};
  template.categories.forEach((cat) => {
    cat.rows.forEach((row) => {
      items[row.id] = {
        status: 'pending',
        reason: '',
        physicalCount: String(Number(row.displayStock) || 0),
      };
    });
  });
  return {
    items,
    startedAt: new Date().toISOString(),
    finishedAt: null,
  };
};

export const getSessionStats = (template, session) => {
  const normalized = normalizeControlTemplate(template);
  const seen = new Set();
  let total = 0;
  let checked = 0;
  normalized.categories.forEach((cat) => {
    cat.rows.forEach((row) => {
      const pid = Number(row.productId);
      const key = Number.isFinite(pid) ? `p-${pid}` : row.id;
      if (seen.has(key)) return;
      seen.add(key);
      total += 1;
      const st = session.items[row.id]?.status;
      if (st === 'ok' || st === 'adjusted') checked += 1;
    });
  });
  return { total, checked, percent: total ? Math.round((checked / total) * 100) : 0 };
};

/**
 * El `template` (catálogo completo: nombre, códigos, ubicación, lotes de cada
 * producto) pesa varios MB con miles de productos y no cambia durante la
 * sesión — solo hace falta mandarlo una vez, al crear. Los guardados
 * posteriores (borrador / finalizar) solo mandan `session` (el progreso real)
 * para no repetir ese payload enorme y superar el límite de tamaño del body.
 */
export const buildControlSnapshot = (template, session, { includeTemplate = true } = {}) => ({
  ...(includeTemplate ? { template } : {}),
  session: pruneUnmodifiedItems(pruneSessionItems(template, session)),
});

export const buildCategoryDisplayRows = (category, session) => {
  let lastModel = null;
  return category.rows.map((row) => {
    const st = session.items[row.id] || { status: 'pending' };
    const showModel = row.model && row.model !== lastModel;
    if (showModel) lastModel = row.model;
    const physical =
      st.physicalCount !== undefined && st.physicalCount !== ''
        ? Number(st.physicalCount)
        : null;
    return {
      ...row,
      showModel: showModel ? row.model : '',
      status: st.status || 'pending',
      reason: st.reason || '',
      physicalCount: st.physicalCount ?? '',
      finalStock:
        physical != null && !Number.isNaN(physical)
          ? physical
          : row.displayStock ?? 0,
    };
  });
};
